import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

import { Settings, Calendar, Plus, Trash2, Users, Building, Briefcase, Shield, Layers, List, Globe, Hash, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const _RCDS_TYPES = [
  { id: 'annuel', label: 'RCDS Annuel', icon: Calendar, color: 'emerald' },
  { id: 'nominatif', label: 'RCDS Nominatif', icon: Briefcase, color: 'blue' }
];

const _ESPACES = [
  { id: 'super_admin', label: 'Super Admin', icon: Shield },
  { id: 'admin', label: 'Admin', icon: Settings },
  { id: 'cooper', label: "Cooper's", icon: Building },
  { id: 'member', label: 'Membre', icon: Users },
  { id: 'expert', label: 'Expert', icon: Briefcase }
];

export default function BaticoTabsB({ ctx }) {
  const { activeRcds, activites, categories, coefficients, config, expandedSections, getAuthHeader, handleUpdateCoefficients, loadData, nomenclatures, setExpandedSections } = ctx;
  return (
    <>
        <TabsContent value="coefficients">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hash className="h-5 w-5 text-blue-500" />
                Coefficients - RCDS {activeRcds === 'annuel' ? 'Annuel' : 'Nominatif'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Par catégorie */}
              <div>
                <h3 className="font-semibold text-slate-700 mb-3">Par Catégorie</h3>
                <div className="grid grid-cols-4 gap-3">
                  {Object.entries(coefficients.par_categorie || {}).map(([cat, coef]) => (
                    <div key={cat} className="p-3 bg-slate-50 rounded-xl">
                      <Label className="text-xs text-slate-500">{cat.replace('_', ' ')}</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={coef}
                        onChange={(e) => {
                          const newCoefs = { ...coefficients };
                          newCoefs.par_categorie[cat] = parseFloat(e.target.value);
                          handleUpdateCoefficients(newCoefs);
                        }}
                        className="mt-1 text-center font-mono"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Par type d'opération */}
              <div>
                <h3 className="font-semibold text-slate-700 mb-3">Par Type d'Opération</h3>
                <div className="grid grid-cols-3 gap-3">
                  {Object.entries(coefficients.par_operation || {}).map(([op, coef]) => (
                    <div key={op} className="p-3 bg-blue-50 rounded-xl">
                      <Label className="text-xs text-blue-600">
                        {op === 'construction_neuve' ? '🏗️ Construction neuve' :
                         op === 'rehabilitation' ? '🔧 Réhabilitation' : '📐 Extension'}
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={coef}
                        onChange={(e) => {
                          const newCoefs = { ...coefficients };
                          newCoefs.par_operation[op] = parseFloat(e.target.value);
                          handleUpdateCoefficients(newCoefs);
                        }}
                        className="mt-1 text-center font-mono"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TYPES OPERATION & TERRITOIRES */}
        <TabsContent value="operations">
          <div className="grid grid-cols-2 gap-6">
            {/* Types d'opération */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-500" />
                  Types d'Opération
                </CardTitle>
                <CardDescription>
                  Options affichées dans le calculateur pour souscriptions nominatives
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {(config?.types_operation || []).map((op) => (
                  <div key={op.id} className="p-4 bg-slate-50 rounded-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={op.actif !== false}
                          onCheckedChange={async () => {
                            try {
                              await axios.put(`${API}/api/batico/config/v3/types-operation/${op.id}`, 
                                { actif: !op.actif }, 
                                { headers: getAuthHeader() }
                              );
                              loadData();
                              toast.success('Type mis à jour');
                            } catch { toast.error('Erreur'); }
                          }}
                        />
                        <div>
                          <span className="font-medium text-slate-800">{op.nom}</span>
                          <p className="text-xs text-slate-500">{op.description}</p>
                        </div>
                      </div>
                      <Badge variant={op.actif ? 'default' : 'secondary'}>
                        Coef: {op.coefficient_base}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">Code</Label>
                        <Input 
                          value={op.code} 
                          readOnly 
                          className="text-xs bg-white/50" 
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Coefficient</Label>
                        <Input 
                          type="number" 
                          step="0.01"
                          value={op.coefficient_base}
                          onChange={async (e) => {
                            try {
                              await axios.put(`${API}/api/batico/config/v3/types-operation/${op.id}`, 
                                { coefficient_base: parseFloat(e.target.value) }, 
                                { headers: getAuthHeader() }
                              );
                              loadData();
                            } catch { toast.error('Erreur'); }
                          }}
                          className="text-xs font-mono"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={async () => {
                            if (window.confirm("Supprimer ce type ?")) {
                              try {
                                await axios.delete(`${API}/api/batico/config/v3/types-operation/${op.id}`, 
                                  { headers: getAuthHeader() }
                                );
                                loadData();
                                toast.success('Type supprimé');
                              } catch { toast.error('Erreur'); }
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Ajouter nouveau type */}
                <div className="p-4 bg-purple-50 rounded-xl border-2 border-dashed border-purple-200">
                  <h4 className="text-sm font-medium text-purple-700 mb-3">Ajouter un type d'opération</h4>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <Input 
                      placeholder="Code (ex: renovation)" 
                      id="newOpCode"
                      className="text-sm"
                    />
                    <Input 
                      placeholder="Nom (ex: Rénovation)" 
                      id="newOpNom"
                      className="text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <Input 
                      placeholder="Description" 
                      id="newOpDesc"
                      className="text-sm"
                    />
                    <Input 
                      type="number"
                      step="0.01"
                      placeholder="Coefficient (ex: 1.1)" 
                      id="newOpCoef"
                      defaultValue="1.0"
                      className="text-sm"
                    />
                  </div>
                  <Button 
                    size="sm" 
                    className="w-full bg-purple-600 hover:bg-purple-700"
                    onClick={async () => {
                      const code = document.getElementById('newOpCode').value;
                      const nom = document.getElementById('newOpNom').value;
                      const desc = document.getElementById('newOpDesc').value;
                      const coef = document.getElementById('newOpCoef').value;
                      if (!code || !nom) { toast.error('Code et nom requis'); return; }
                      try {
                        await axios.post(`${API}/api/batico/config/v3/types-operation`, 
                          { code, nom, description: desc, coefficient_base: parseFloat(coef) || 1.0 }, 
                          { headers: getAuthHeader() }
                        );
                        loadData();
                        toast.success('Type ajouté');
                        document.getElementById('newOpCode').value = '';
                        document.getElementById('newOpNom').value = '';
                        document.getElementById('newOpDesc').value = '';
                        document.getElementById('newOpCoef').value = '1.0';
                      } catch { toast.error('Erreur'); }
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" /> Ajouter
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Territoires d'intervention */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-blue-500" />
                  Territoires d'Intervention
                  <Badge variant="outline" className="ml-2 text-[10px] font-medium border-blue-300 text-blue-700 bg-blue-50">
                    Nominative
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Options de territoire pour les souscriptions <strong>RCDS Nominative</strong>,
                  <strong> Avenant Nominatif</strong> et <strong>PIB Nominatif</strong> (Polices d&apos;Immeuble en Construction).
                  <span className="block mt-1 text-[11px] text-blue-700 italic">
                    Les calculateurs <strong>RCDS Annuel / PIB Annuel / Avenant Annuel</strong> utilisent une
                    liste distincte gérée dans l&apos;onglet <em>Modulateurs éligibilité → Territoires</em>.
                  </span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {(config?.territoires_intervention || []).map((terr) => (
                  <div key={terr.id} className="p-4 bg-slate-50 rounded-xl space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={terr.actif !== false}
                          onCheckedChange={async () => {
                            try {
                              await axios.put(`${API}/api/batico/config/v3/territoires/${terr.id}`, 
                                { actif: !terr.actif }, 
                                { headers: getAuthHeader() }
                              );
                              loadData();
                              toast.success('Territoire mis à jour');
                            } catch { toast.error('Erreur'); }
                          }}
                        />
                        <div>
                          <span className="font-medium text-slate-800">{terr.nom}</span>
                          <p className="text-xs text-slate-500">{terr.description}</p>
                        </div>
                      </div>
                      <Badge variant={terr.actif ? 'default' : 'secondary'}>
                        Coef: {terr.coefficient_base}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">Code</Label>
                        <Input 
                          value={terr.code} 
                          readOnly 
                          className="text-xs bg-white/50" 
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Coefficient</Label>
                        <Input 
                          type="number" 
                          step="0.01"
                          value={terr.coefficient_base}
                          onChange={async (e) => {
                            try {
                              await axios.put(`${API}/api/batico/config/v3/territoires/${terr.id}`, 
                                { coefficient_base: parseFloat(e.target.value) }, 
                                { headers: getAuthHeader() }
                              );
                              loadData();
                            } catch { toast.error('Erreur'); }
                          }}
                          className="text-xs font-mono"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={async () => {
                            if (window.confirm("Supprimer ce territoire ?")) {
                              try {
                                await axios.delete(`${API}/api/batico/config/v3/territoires/${terr.id}`, 
                                  { headers: getAuthHeader() }
                                );
                                loadData();
                                toast.success('Territoire supprimé');
                              } catch { toast.error('Erreur'); }
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Ajouter nouveau territoire */}
                <div className="p-4 bg-blue-50 rounded-xl border-2 border-dashed border-blue-200">
                  <h4 className="text-sm font-medium text-blue-700 mb-3">Ajouter un territoire</h4>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <Input 
                      placeholder="Code (ex: outre_mer)" 
                      id="newTerrCode"
                      className="text-sm"
                    />
                    <Input 
                      placeholder="Nom (ex: Outre-Mer)" 
                      id="newTerrNom"
                      className="text-sm"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <Input 
                      placeholder="Description" 
                      id="newTerrDesc"
                      className="text-sm"
                    />
                    <Input 
                      type="number"
                      step="0.01"
                      placeholder="Coefficient (ex: 1.1)" 
                      id="newTerrCoef"
                      defaultValue="1.0"
                      className="text-sm"
                    />
                  </div>
                  <Button 
                    size="sm" 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={async () => {
                      const code = document.getElementById('newTerrCode').value;
                      const nom = document.getElementById('newTerrNom').value;
                      const desc = document.getElementById('newTerrDesc').value;
                      const coef = document.getElementById('newTerrCoef').value;
                      if (!code || !nom) { toast.error('Code et nom requis'); return; }
                      try {
                        await axios.post(`${API}/api/batico/config/v3/territoires`, 
                          { code, nom, description: desc, coefficient_base: parseFloat(coef) || 1.0 }, 
                          { headers: getAuthHeader() }
                        );
                        loadData();
                        toast.success('Territoire ajouté');
                        document.getElementById('newTerrCode').value = '';
                        document.getElementById('newTerrNom').value = '';
                        document.getElementById('newTerrDesc').value = '';
                        document.getElementById('newTerrCoef').value = '1.0';
                      } catch { toast.error('Erreur'); }
                    }}
                  >
                    <Plus className="h-4 w-4 mr-2" /> Ajouter
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* NOMENCLATURES */}
        <TabsContent value="nomenclatures">
          <div className="grid grid-cols-3 gap-4">
            {/* Nomenclatures */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Layers className="h-4 w-4 text-amber-500" />
                  Nomenclatures
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {nomenclatures.map(nom => (
                  <div key={nom.id} className={`p-3 rounded-lg cursor-pointer transition-all ${
                    expandedSections.nom === nom.id ? 'bg-amber-100 border-2 border-amber-300' : 'bg-slate-50 hover:bg-slate-100'
                  }`} onClick={() => setExpandedSections(p => ({ ...p, nom: nom.id }))}>
                    <div className="font-medium">{nom.code}</div>
                    <div className="text-sm text-slate-500">{nom.nom}</div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Catégories */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <List className="h-4 w-4 text-purple-500" />
                  Catégories
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {categories.filter(c => !expandedSections.nom || c.nomenclature_id === expandedSections.nom).map(cat => (
                  <div key={cat.id} className={`p-3 rounded-lg cursor-pointer transition-all ${
                    expandedSections.cat === cat.id ? 'bg-purple-100 border-2 border-purple-300' : 'bg-slate-50 hover:bg-slate-100'
                  }`} onClick={() => setExpandedSections(p => ({ ...p, cat: cat.id }))}>
                    <div className="font-medium">{cat.code}</div>
                    <div className="text-sm text-slate-500">{cat.nom}</div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Activités */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-blue-500" />
                  Activités
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-96 overflow-y-auto">
                {activites.filter(a => !expandedSections.cat || a.categorie_id === expandedSections.cat).map(act => (
                  <div key={act.id} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{act.nom}</div>
                      <Badge variant="outline" className="text-xs">{act.coefficient}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{act.definition}</div>
                    {act.exclusions?.length > 0 && (
                      <div className="text-xs text-red-500 mt-1">
                        Exclusions: {act.exclusions.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* SYNC */}
    </>
  );
}
